import { P2GBox } from '@parcel2go/core-ui';
import { P2GTextField } from '@parcel2go/core-ui';
import { useEffect, useMemo, useState } from 'react';
import { ExtraBase, QuoteApiModel, QuoteOptionApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import Loading from '../../../../components/loading/Loading';
import { generateProtectionPercentageKey } from './generateProtectionKey';
import ServiceInformationModal from './item/ServiceInformationModal';
import { ProtectionSelectionDisplay } from './ProtectionSelectionDisplay';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

interface props {
  onUpdate?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  getQuote: () => Promise<QuoteApiModel>;
  currentSelection: ExtraBase[];
  currentCoverPercentage: number;
  item: SmartSendItemApiModel;
  includeServiceModel: boolean;
}

export const ProtectionSelection: React.FC<props> = (props) => {
  const [loading, setLoading] = useState(true);
  const [quote, setQuotes] = useState<QuoteApiModel>();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const [protectionKey, setProtectionKey] = useState<string>(
    generateProtectionPercentageKey(props.currentCoverPercentage)
  );

  useEffect(() => {
    async function getData() {
      const r = await props.getQuote();
      setQuotes(r);
      setLoading(false);
    }
    getData();
  }, []);

  useEffect(() => {
    if (props.onUpdate && quote) {
      const r = quote.Options.find((x) => generateProtectionPercentageKey(x.CoverPercentage) === protectionKey);
      if (r) {
        props.onUpdate(quote, r);
      }
    }
  }, [protectionKey, quote]);

  const options = useMemo(() => {
    if (!quote?.Options) {
      return [];
    }
    return quote.Options.map((o) => {
      return { ...o, Key: generateProtectionPercentageKey(o.CoverPercentage) };
    }).sort((a, b) => a.CoverPercentage - b.CoverPercentage);
  }, [quote]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {options.length > 0 ? (
        <ProtectionSelectionDisplay
          currencyCode={quote?.CurrencyCode}
          parcelValue={props.item.Value}
          selectedKey={protectionKey}
          setSelected={setProtectionKey}
          options={options}
        />
      ) : (
        <P2GTextField
          fullWidth
          label={t('QUOTE_INPUT_LABEL', { defaultValue: 'Quote' })}
          value={
            loading
              ? t('LOADING_TEXT', { defaultValue: 'Loading...' })
              : t('NO_QUOTE_ERROR', { defaultValue: 'No Quote' })
          }
          onChange={() => {}}
        />
      )}
      {props.includeServiceModel && (
        <P2GBox mt={1}>
          <ServiceInformationModal
            itemId={props.item.Id}
            service={props.item.Service}
            recipientIsoCode={props.item.Recipient.CountryIsoCode}
            recipientSubdivision={props.item.Recipient.CountrySubdivision}
            senderIsoCode={props.item.Sender.CountryIsoCode}
            senderSubdivision={props.item.Sender.CountrySubdivision}
            dims={props.item.PackageDimensions}
            label={t('VIEW_SERVICE_DETAILS_LINK', { defaultValue: 'View service details' })}
            currencyCode={quote?.CurrencyCode}
          />
        </P2GBox>
      )}
    </>
  );
};
